import { isSharedDataset as isSharedDatasetUtils } from 'js/lib/URLUtils';

/**
 * Shared Dataset Helper Functions
 *
 * This module contains utility functions for managing shared dataset nodes and edges.
 *
 * Key Functions:
 * - createSharedDataSetEdge: Creates edges between shared datasets and created datasets
 * - adjustSharedDatasetPositions: Adjusts positioning of shared dataset nodes
 * - isSharedDataset: Type guard to check if a node is a shared dataset
 *
 * See docs/FlowNodesArchitecture.md for architecture overview.
 */

import type { Edge, Node } from '@xyflow/react';
import { MarkerType } from '@xyflow/react';
import { getColorFromItemStatus } from './helpers';
import { labelBgStyles, labelStyles, NODE_HEIGHT, NODE_WIDTH } from './consts';

const SHARED_DATASET_SPACING = 50;

const isEdgeExists = (edges: Edge[], edgeId: string): boolean => edges.some((e) => e.id === edgeId);

const warningMessage = (sharedDataSetId: string): string =>
  `[FlowNodes] Skipping shared dataset edge creation: shared dataset ${sharedDataSetId} not found in entities`;

export function isSharedDataset(node: Node): boolean {
  return isSharedDatasetUtils(node.data?.item as any);
}

export function createSharedDataSetEdge(
  item: any,
  edges: Edge[],
  {
    entities,
    existingNodeIds,
    expandedNodes,
    isSecondaryFlow,
  }: {
    entities: any;
    existingNodeIds: Set<string>;
    expandedNodes: string[];
    isSecondaryFlow: boolean;
  },
): void {
  const hasSharedDataSetId = item.shared_data_set_id;
  const hasFlowNodeId = item.flow_node_id;
  if (!hasSharedDataSetId || !hasFlowNodeId) return;

  const sharedDataSetId = item.shared_data_set_id;
  const sharedDataSet = entities?.dataSets?.[sharedDataSetId];
  if (!sharedDataSet) {
    console.warn(warningMessage(sharedDataSetId));
    return;
  }

  const sourceNodeId = `node-${item.shared_data_set_id}`; // Shared dataset node ID
  const targetNodeId = `node-${item.flow_node_id}`; // Created dataset node ID (from shared dataset)

  if (existingNodeIds.size > 0) {
    if (!existingNodeIds.has(sourceNodeId) || !existingNodeIds.has(targetNodeId)) return;
  }

  const edgeId = `edge-shared-dataset-${item.flow_node_id}`;
  if (isEdgeExists(edges, edgeId)) return; // Skip edge creation if edge already exists

  const expanded = expandedNodes.includes('flow-' + (item.origin_node_id || item.id));

  const edgeStatus = 'RELATION';
  const edgeLabel = 'Shared';

  const edge = {
    id: edgeId,
    className: 'edge-status-' + edgeStatus,
    source: sourceNodeId,
    target: targetNodeId,
    selectable: false,
    deletable: false,
    focusable: false,
    reconnectable: false,
    label: edgeLabel,
    labelShowBg: true,
    labelBgBorderRadius: 4,
    labelBgPadding: [4, 4],
    labelBgStyle: labelBgStyles[edgeStatus],
    labelStyle: labelStyles[edgeStatus],
    zIndex: isSecondaryFlow && expanded ? 3001 : 2,
    markerEnd: {
      type: MarkerType.Arrow,
      color: getColorFromItemStatus(edgeStatus),
      strokeWidth: 2,
    },
    targetHandle: 'left',
    sourceHandle: 'right',
  } as Edge;

  edges.push(edge);
}

export function adjustSharedDatasetPositions(
  nodes: Node[],
  edges: Edge[],
  options?: { rankdir?: string },
): void {
  const sharedDatasetNodes = nodes.filter((node) => isSharedDataset(node));

  if (sharedDatasetNodes.length === 0) return;

  const isTB = options?.rankdir === 'TB';

  sharedDatasetNodes.forEach((sharedNode) => {
    const connectingEdge = edges.find(
      (edge) => edge.source === sharedNode.id && edge.id.startsWith('edge-shared-dataset-'),
    );

    if (!connectingEdge) return;

    const targetNode = nodes.find((n) => n.id === connectingEdge.target);

    if (!targetNode) return;

    if (isTB) {
      sharedNode.position.x = targetNode.position.x;
      sharedNode.position.y = targetNode.position.y - NODE_HEIGHT - SHARED_DATASET_SPACING;
    } else {
      sharedNode.position.x = targetNode.position.x - NODE_WIDTH - SHARED_DATASET_SPACING;
      sharedNode.position.y = targetNode.position.y;
    }
  });
}
